import React, { ReactElement, useMemo, useState } from 'react'
import { sort } from 'ramda'
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'

import {
  AccreditationHealthRow,
  AccreditationHealthStatus,
  AccreditationKey,
  ACCREDITATION_COLUMNS,
} from 'src/v3/content-pipeline/accreditation-health.slice'
import {
  accreditationSeverityScore,
  isFullyCurrent,
} from 'src/v3/content-pipeline/accreditation/utils/accreditation-status-meta'
import {
  AccreditationPendingChip,
  AccreditationStatusChip,
} from './accreditation-status-chip'
import AccreditationNotPursuedToggle from './accreditation-not-pursued-toggle'

let STATUS_FILTER_OPTIONS: {
  value: AccreditationHealthStatus | 'all'
  label: string
}[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'missing', label: 'Missing' },
  { value: 'notPursued', label: 'Not Pursued' },
  { value: 'expired', label: 'Expired' },
  { value: 'approaching', label: 'Approaching' },
  { value: 'current', label: 'Current' },
  { value: 'noExpirationDate', label: 'No Expiration' },
]

type Props = {
  rows: AccreditationHealthRow[]
  domain: 'live' | 'enduring'
  noun: 'course' | 'event'
  accreditationFilter: AccreditationKey | 'all'
  onAccreditationFilterChange: (value: AccreditationKey | 'all') => void
  onSelectRow: (id: string) => void
}

export default function AccreditationNeedsAttentionTab({
  rows,
  domain,
  noun,
  accreditationFilter,
  onAccreditationFilterChange,
  onSelectRow,
}: Props): ReactElement {
  let [statusFilter, setStatusFilter] = useState<
    AccreditationHealthStatus | 'all'
  >('all')
  let [search, setSearch] = useState('')
  let [onlyPending, setOnlyPending] = useState(false)
  let [includeCurrent, setIncludeCurrent] = useState(false)

  let allRecords = useMemo(
    () =>
      rows.flatMap((row) =>
        ACCREDITATION_COLUMNS.map((col) => ({
          rowId: row.id,
          rowLabel: row.label,
          key: col.key,
          label: col.label,
          cell: row.accreditations[col.key],
        })),
      ),
    [rows],
  )

  let filtered = useMemo(() => {
    let query = search.trim().toLowerCase()
    let result = allRecords.filter(
      (r) =>
        (accreditationFilter === 'all' || r.key === accreditationFilter) &&
        (statusFilter === 'all' || r.cell.status === statusFilter) &&
        (!query || r.rowLabel.toLowerCase().includes(query)) &&
        (!onlyPending || r.cell.pending) &&
        (includeCurrent || !isFullyCurrent(r.cell.status, r.cell.pending)),
    )
    return sort(
      (a, b) =>
        accreditationSeverityScore(a.cell.status, a.cell.pending) -
        accreditationSeverityScore(b.cell.status, b.cell.pending),
      result,
    )
  }, [
    allRecords,
    accreditationFilter,
    statusFilter,
    search,
    onlyPending,
    includeCurrent,
  ])

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="needs-attention-accreditation-filter-label">
            Accreditation body
          </InputLabel>
          <Select
            labelId="needs-attention-accreditation-filter-label"
            label="Accreditation body"
            value={accreditationFilter}
            onChange={(e) =>
              onAccreditationFilterChange(
                e.target.value as AccreditationKey | 'all',
              )
            }
          >
            <MenuItem value="all">All bodies</MenuItem>
            {ACCREDITATION_COLUMNS.map((col) => (
              <MenuItem key={col.key} value={col.key}>
                {col.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="needs-attention-status-filter-label">
            Current status
          </InputLabel>
          <Select
            labelId="needs-attention-status-filter-label"
            label="Current status"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                e.target.value as AccreditationHealthStatus | 'all',
              )
            }
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          sx={{ minWidth: 220, flex: 1 }}
          label={`Search ${noun}`}
          placeholder={`${noun === 'course' ? 'Course' : 'Event'} name...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={onlyPending}
              onChange={(e) => setOnlyPending(e.target.checked)}
            />
          }
          label="Only with a pending update"
        />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeCurrent}
              onChange={(e) => setIncludeCurrent(e.target.checked)}
            />
          }
          label="Include current & not pursued"
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {filtered.length} of {allRecords.length} accreditation records match —
        sorted worst first.
      </Typography>

      <TableContainer sx={{ maxHeight: 520, overflowY: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{noun === 'course' ? 'Course' : 'Event'}</TableCell>
              <TableCell>Accreditation</TableCell>
              <TableCell>Current Status</TableCell>
              <TableCell>In-Flight Update</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{ py: 2, textAlign: 'center' }}
                  >
                    No records match these filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((record) => (
                <TableRow
                  key={`${record.rowId}-${record.key}`}
                  hover
                  onClick={() => onSelectRow(record.rowId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelectRow(record.rowId)
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`View ${record.label} details for ${record.rowLabel}`}
                  sx={{
                    cursor: 'pointer',
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '-2px',
                    },
                  }}
                >
                  <TableCell>{record.rowLabel}</TableCell>
                  <TableCell>{record.label}</TableCell>
                  <TableCell>
                    <AccreditationStatusChip status={record.cell.status} />
                  </TableCell>
                  <TableCell>
                    {record.cell.pending ? (
                      <AccreditationPendingChip pending={record.cell.pending} />
                    ) : (
                      <Typography color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                      }}
                    >
                      <Typography color="primary" variant="body2">
                        {record.cell.pending ? 'Review draft' : 'Create update'}
                      </Typography>
                      {(record.cell.status === 'missing' ||
                        record.cell.status === 'notPursued') && (
                        <AccreditationNotPursuedToggle
                          domain={domain}
                          rowId={record.rowId}
                          slot={record.key}
                          isFlagged={record.cell.status === 'notPursued'}
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
